import { normalizeCategoryType } from './tagValueUtils';

// A table's ingestion settings (Spec 6 section 2.3). The browser writes each of
// them whole, so Table Settings refuses on save what the backend's
// validate_table_settings (common/column_types.py) would refuse before the
// landing builds any SQL: the same checks, in the same order.
//
//   column_policies      {column: {policy, half_life_days?}}, time series only
//   reporting_date_mode  latest | earliest | most_common
//   classification       {column: {classes: {class: [aliases]}, threshold}}
//   entityResolution     {threshold}, time series only

export const DETAIL_POLICIES = [
  'most_reported', 'most_reported_recent_weighted', 'first_reported', 'last_reported', 'must_agree',
];
export const ATTRIBUTE_POLICIES = ['last_reported', 'must_agree'];
export const OVERWRITE_METRIC_POLICIES = ['last_reported', 'first_reported'];
export const RECENT_WEIGHTED_POLICY = 'most_reported_recent_weighted';
export const REPORTING_DATE_MODES = ['latest', 'earliest', 'most_common'];

// Each column policy (Spec 3 section 3) by the name the person reads, in Table
// Settings and in the cell lineage drawer.
export const POLICY_LABELS = {
  most_reported: 'Most reported',
  most_reported_recent_weighted: 'Most reported, recent files weighted',
  first_reported: 'First reported',
  last_reported: 'Last reported',
  must_agree: 'Must agree',
};

// Every Overwrite metric leaf of a metric schema, as a dotted path
// ('Claims.Reserve'). An Add metric is always summed and has no policy.
export function overwriteMetricPaths(metricSchema, prefix = '') {
  const paths = [];
  Object.entries(metricSchema || {}).forEach(([name, definition]) => {
    const path = prefix ? `${prefix}.${name}` : name;
    if (Object.prototype.hasOwnProperty.call(definition, 'type')) {
      if ((definition.updateBehavior || 'Overwrite') === 'Overwrite') paths.push(path);
    } else {
      paths.push(...overwriteMetricPaths(definition, path));
    }
  });
  return paths;
}

// The policies each column of a time-series table may take, in schema order:
// [{column, kind, choices}] for every detail, attribute and Overwrite metric leaf.
export function policyColumns(table) {
  const columns = [];
  (table.detailColumns || []).forEach((column) => {
    columns.push({ column: column.name, kind: 'detail', choices: DETAIL_POLICIES });
  });
  (table.attributeColumns || []).forEach((column) => {
    columns.push({ column: column.name, kind: 'attribute', choices: ATTRIBUTE_POLICIES });
  });
  overwriteMetricPaths(table.metricColumns?.schema).forEach((path) => {
    columns.push({ column: path, kind: 'metric', choices: OVERWRITE_METRIC_POLICIES });
  });
  return columns;
}

// A similarity threshold: a number above 0 and at most 1, never a boolean.
export const isThreshold = (value) => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 1
);

const isPlainObject = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const sameKeys = (object, keys) => {
  const present = Object.keys(object).sort();
  const expected = [...keys].sort();
  return present.length === expected.length && present.every((key, index) => key === expected[index]);
};

// The first part of a classification that is not valid, or null. Declared at
// creation on Text detail columns only (CreateTableForm.js); Table Settings
// edits only the aliases.
export function classificationError(table) {
  const textDetails = new Set((table.detailColumns || [])
    .filter((column) => normalizeCategoryType(column.type) === 'Text')
    .map((column) => column.name));
  const { classification } = table;
  if (!isPlainObject(classification)) return 'The classification must be a map of column names.';
  for (const [column, configuration] of Object.entries(classification)) {
    if (!textDetails.has(column)) return `The classified column ${column} is not a Text detail column of the table.`;
    if (!isPlainObject(configuration) || !sameKeys(configuration, ['classes', 'threshold'])) {
      return `The classification of ${column} must hold exactly its classes and its threshold.`;
    }
    if (!isThreshold(configuration.threshold)) {
      return `The classification threshold of ${column} must be a number above 0 and at most 1.`;
    }
    const { classes } = configuration;
    if (!isPlainObject(classes) || Object.keys(classes).length === 0) {
      return `The classification of ${column} needs at least one class.`;
    }
    for (const [className, aliases] of Object.entries(classes)) {
      if (!className.trim()) return `A class of ${column} has no name.`;
      const allText = Array.isArray(aliases)
        && aliases.every((alias) => typeof alias === 'string' && alias.trim() !== '');
      if (!allText) return `The aliases of the class ${className} of ${column} must be a list of text.`;
    }
  }
  return null;
}

// The first table setting that is not valid, or null, for a native table
// document as it would be saved.
export function tableSettingsError(table) {
  if (!REPORTING_DATE_MODES.includes(table.reporting_date_mode)) {
    return `The reporting date mode must be one of ${REPORTING_DATE_MODES.join(', ')}.`;
  }
  const classificationProblem = classificationError(table);
  if (classificationProblem) return classificationProblem;
  if (table.tableType !== 'timeSeries') return null;

  const allowed = new Map(policyColumns(table).map(({ column, choices }) => [column, choices]));
  const policies = table.column_policies;
  if (!isPlainObject(policies)) return 'The column policies must be a map of column names.';
  for (const column of Object.keys(policies)) {
    if (!allowed.has(column)) {
      return `A column policy names ${column}, which is not a detail, attribute or Overwrite metric column of the table.`;
    }
  }
  for (const [column, choices] of allowed) {
    const entry = policies[column];
    if (!isPlainObject(entry) || !choices.includes(entry.policy)) {
      return `The column ${column} needs a policy, one of ${choices.join(', ')}.`;
    }
    const weighted = entry.policy === RECENT_WEIGHTED_POLICY;
    if (!sameKeys(entry, weighted ? ['policy', 'half_life_days'] : ['policy'])) {
      return `The policy of ${column} must have a half-life exactly when it is recent-weighted.`;
    }
    if (weighted) {
      const halfLife = entry.half_life_days;
      if (typeof halfLife !== 'number' || !Number.isFinite(halfLife) || halfLife <= 0) {
        return `The half-life of ${column} must be a number of days above zero.`;
      }
    }
  }

  const resolution = table.entityResolution;
  if (!isPlainObject(resolution) || !sameKeys(resolution, ['threshold']) || !isThreshold(resolution.threshold)) {
    return 'The entity resolution threshold must be a number above 0 and at most 1.';
  }
  return null;
}
